import mixpanel from 'mixpanel-browser';
import { Inject, Injectable } from '@angular/core';
import { Configuration } from '../constant/configuration';
import { CurrentUserI } from '../models/user';

export interface MixpanelConstants {
    prodToken: string;
    prodDomain: string;
    uatToken: string;
    uatDomain: string;
    actions: Configuration['mixpanel']['actions'];
}

@Injectable({ providedIn: 'root' })
export class MixpanelService {
    constants: MixpanelConstants;
    isInitialized = false;
    protected validTimeout: ReturnType<typeof setTimeout>;
    private readonly DOC_STATS_THRESHOLD: number;
    private currentUser: CurrentUserI;

    constructor(@Inject('window') private window: Window, configuration: Configuration) {
        this.constants = configuration.mixpanel;
        this.DOC_STATS_THRESHOLD = configuration.VALID_VIEW_THRESHOLD;
    }

    initialize(): void {
        const token = this.chooseToken();
        if (token) {
            mixpanel.init(token, {
                api_host: 'https://api-eu.mixpanel.com',
                opt_out_tracking_by_default: true
            });
            mixpanel.set_config({
                batch_flush_interval_ms: 1000
            });
            this.isInitialized = true;
        }
    }

    optIn(): void {
        if (this.isInitialized) {
            mixpanel.opt_in_tracking();
            if (this.currentUser) {
                this.identifyUser(this.currentUser);
            }
        }
    }

    optOut(): void {
        if (this.isInitialized) {
            mixpanel.opt_out_tracking();
        }
    }

    identify(userRaw: CurrentUserI): void {
        this.currentUser = userRaw;
        if (this.isInitialized && mixpanel.has_opted_in_tracking()) {
            this.identifyUser(userRaw);
        }
    }

    private identifyUser(userRaw: CurrentUserI): void {
        const user = {
            $email: userRaw.email,
            $name: userRaw.name + ' ' + userRaw.firstname,
            organizationId: userRaw.companyUuid,
            registrationDate: userRaw.termsAndAgreementsDate,
            userRole: userRaw.roles.join(' | '),
            $distinct_id: userRaw.uuid,
            $set: {
                $email: userRaw.email
            },
            organizationName: userRaw.companyName,
            preferredLanguage: userRaw.language,
            organizationSector: userRaw.companySector,
            authenticationType: userRaw.authenticationType || 'NOT LOGGED',
            market: userRaw.marketsCodes.join(' | '),
            freeTrial: userRaw.companyOnTrial
        };

        if (this.isInitialized) {
            mixpanel.identify(user.$distinct_id); // identify user
            mixpanel.register(user); // sets common data for all events
            mixpanel.people.set_once(user); // adds a person to global mixpanel if it does not exists
        }
    }

    track(actionType: string, actionProps?: Record<string, string | number | boolean>): void {
        if (this.isInitialized && mixpanel.has_opted_in_tracking()) {
            const time = new Date().toISOString();
            mixpanel.track(actionType, { ...actionProps, timestamp: time });
        }
    }

    delayedTrackView(actionType: string, actionProps?: Record<string, string | number | boolean>): void {
        this.destroyDelayedTrack();
        this.validTimeout = setTimeout(() => {
            this.track(actionType, actionProps);
        }, this.DOC_STATS_THRESHOLD);
    }

    /**
     * Remove setTimeout set by delayedTrack
     */
    public destroyDelayedTrack(): void {
        if (this.validTimeout) {
            clearTimeout(this.validTimeout);
        }
    }

    reset(): void {
        if (this.isInitialized) {
            mixpanel.reset();
        }
    }

    chooseToken(): string {
        let token: string = null;
        if (this.window.location.hostname.toLowerCase().search(this.constants.uatDomain) >= 0) {
            token = this.constants.uatToken;
        } else if (this.window.location.hostname.toLowerCase().search(this.constants.prodDomain) >= 0) {
            token = this.constants.prodToken;
        }
        return token;
    }
}
